import * as React from "react";
import { getUsage, Usage } from "../utils/api";
import { useAuth } from "./AuthContext";

export type UsageStatus = "idle" | "loading" | "loaded" | "error";

export type UsageContextValue = {
  usage: Usage | null;
  status: UsageStatus;
  refresh: () => Promise<void>;
};

// A real default (rather than throwing) keeps SSR/prerender of any consumer
// safe: consumers render nothing until the provider hydrates and loads.
const UsageContext = React.createContext<UsageContextValue>({
  usage: null,
  status: "idle",
  refresh: async () => {},
});

export function UsageProvider({ children }: { children: React.ReactNode }) {
  const { status: authStatus } = useAuth();
  const [usage, setUsage] = React.useState<Usage | null>(null);
  const [status, setStatus] = React.useState<UsageStatus>("idle");

  const refresh = React.useCallback(async () => {
    setStatus("loading");
    const result = await getUsage().catch(() => null);
    if (result?.data) {
      setUsage(result.data);
      setStatus("loaded");
    } else {
      setUsage(null);
      setStatus("error");
    }
  }, []);

  React.useEffect(() => {
    if (authStatus === "authenticated") {
      refresh();
    } else if (authStatus === "anonymous") {
      setUsage(null);
      setStatus("idle");
    }
  }, [authStatus, refresh]);

  const value = React.useMemo(
    () => ({ usage, status, refresh }),
    [usage, status, refresh]
  );

  return (
    <UsageContext.Provider value={value}>{children}</UsageContext.Provider>
  );
}

export const useUsage = () => React.useContext(UsageContext);
